import { z } from 'zod';
import { parseMailboxAddress } from '../mailbox-address.js';

const DraftRecipient = z.string().trim().min(1).max(512);
const DraftRecipients = z.array(DraftRecipient).max(100);
const EmailAddress = z.email().max(320);
export const SendRecipient = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .superRefine((value, ctx) => {
    const parsed = parseMailboxAddress(value);
    if (!parsed || !EmailAddress.safeParse(parsed.email).success) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a valid email address or use Name <email@example.com>',
      });
    }
  });
const SendRecipients = z.array(SendRecipient).max(100);

export const MAX_DRAFT_ATTACHMENT_COUNT = 20;
export const MAX_DRAFT_ATTACHMENT_BYTES = 18 * 1024 * 1024;
export const MAX_DRAFT_ATTACHMENT_TOTAL_BYTES = 18 * 1024 * 1024;
export const MAX_DRAFT_BODY_TEXT_LENGTH = 1_000_000;
export const MAX_DRAFT_BODY_HTML_LENGTH = 2_000_000;

export const DraftComposeKind = z.enum(['new', 'reply', 'reply_all', 'forward']);
export type DraftComposeKind = z.infer<typeof DraftComposeKind>;

export const DraftAttachment = z.object({
  id: z.number().int().positive(),
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  size: z.number().int().nonnegative().max(MAX_DRAFT_ATTACHMENT_BYTES),
  isInline: z.boolean(),
});
export type DraftAttachment = z.infer<typeof DraftAttachment>;

export const DraftProviderSyncState = z.enum([
  'pending',
  'inflight',
  'synced',
  'failed',
  'unsupported',
]);
export type DraftProviderSyncState = z.infer<typeof DraftProviderSyncState>;

export const DraftSendState = z.enum(['draft', 'scheduled', 'sending']);
export type DraftSendState = z.infer<typeof DraftSendState>;

export const Draft = z.object({
  senderIdentityId: z.uuid().nullable().optional(),
  id: z.number().int().positive(),
  kind: DraftComposeKind,
  sourceMessageId: z.number().int().positive().nullable(),
  accountId: z.number().int().positive(),
  to: DraftRecipients,
  cc: DraftRecipients,
  bcc: DraftRecipients,
  subject: z.string().max(998),
  bodyHtml: z.string().max(MAX_DRAFT_BODY_HTML_LENGTH),
  bodyText: z.string().max(MAX_DRAFT_BODY_TEXT_LENGTH),
  attachments: z.array(DraftAttachment).max(MAX_DRAFT_ATTACHMENT_COUNT),
  providerSyncState: DraftProviderSyncState,
  providerSyncError: z.string().nullable(),
  sendError: z.string().nullable(),
  sendState: DraftSendState.default('draft'),
  scheduledAt: z.number().int().nonnegative().nullable().default(null),
  sendRequestedAt: z.number().int().nonnegative().nullable().default(null),
  updatedAt: z.number().int().nonnegative(),
});
export type Draft = z.infer<typeof Draft>;

export const DraftList = z.array(Draft);

export const DraftCreate = z.object({
  senderIdentityId: z.uuid().nullable().optional(),
  accountId: z.number().int().positive(),
  to: SendRecipients.optional(),
});
export type DraftCreate = z.infer<typeof DraftCreate>;

export const DraftFromMessageCreate = z.object({
  messageId: z.number().int().positive(),
  mode: DraftComposeKind.exclude(['new']),
});
export type DraftFromMessageCreate = z.infer<typeof DraftFromMessageCreate>;

export const DraftImportCreate = z.object({
  messageId: z.number().int().positive(),
});
export type DraftImportCreate = z.infer<typeof DraftImportCreate>;

export const DraftPatch = z.object({
  senderIdentityId: z.uuid().nullable().optional(),
  accountId: z.number().int().positive().optional(),
  to: DraftRecipients.optional(),
  cc: DraftRecipients.optional(),
  bcc: DraftRecipients.optional(),
  subject: z.string().max(998).optional(),
  bodyHtml: z.string().max(MAX_DRAFT_BODY_HTML_LENGTH).optional(),
  bodyText: z.string().max(MAX_DRAFT_BODY_TEXT_LENGTH).optional(),
});
export type DraftPatch = z.infer<typeof DraftPatch>;

export const DraftSendRequest = z
  .object({
    senderIdentityId: z.uuid().nullable().optional(),
    accountId: z.number().int().positive(),
    to: SendRecipients,
    cc: SendRecipients,
    bcc: SendRecipients,
    subject: z.string().max(998),
    // The default keeps a rolling deployment compatible with an older client.
    bodyHtml: z.string().max(MAX_DRAFT_BODY_HTML_LENGTH).default(''),
    bodyText: z.string().max(MAX_DRAFT_BODY_TEXT_LENGTH),
  })
  .superRefine((value, ctx) => {
    const recipientCount = value.to.length + value.cc.length + value.bcc.length;
    if (recipientCount === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Add at least one recipient',
        path: ['to'],
      });
    }
    if (recipientCount > 100) {
      ctx.addIssue({
        code: 'custom',
        message: 'A message can have at most 100 recipients',
        path: ['to'],
      });
    }
  });
export type DraftSendRequest = z.infer<typeof DraftSendRequest>;

export const DraftScheduleRequest = z.intersection(
  DraftSendRequest,
  z.object({ sendAt: z.number().int().nonnegative() }),
);
export type DraftScheduleRequest = z.infer<typeof DraftScheduleRequest>;

export const DraftSendResponse = z.object({
  ok: z.literal(true),
  outboxId: z.number().int().positive(),
  sentCopyPending: z.boolean(),
});
export type DraftSendResponse = z.infer<typeof DraftSendResponse>;

export const DraftDeferredSendResponse = z.object({
  ok: z.literal(true),
  state: z.literal('scheduled'),
  draft: Draft,
  scheduledAt: z.number().int().nonnegative(),
  undoUntil: z.number().int().nonnegative(),
});
export type DraftDeferredSendResponse = z.infer<typeof DraftDeferredSendResponse>;

export const DraftSendAcceptedResponse = z.union([
  DraftSendResponse,
  DraftDeferredSendResponse,
]);
export type DraftSendAcceptedResponse = z.infer<typeof DraftSendAcceptedResponse>;
